import { useSeoMeta } from '@unhead/react';
import { useEffect, useRef, useState } from 'react';
import {
  ExternalLink,
  Menu,
  X,
  GitBranch,
  Shield,
  Lock,
  Code2,
  MessageSquare,
  Zap,
  Key,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SiteFooter } from '@/components/SiteFooter';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */
const GithubIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Nori SVG Mascot                                                    */
/* ------------------------------------------------------------------ */
const NoriLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg
    viewBox="0 0 20 16"
    shapeRendering="crispEdges"
    aria-label="Nori the ostrich"
    className={className}
  >
    <g fill="#7B68EE">
      <rect x="8" y="0" width="2" height="1"/>
      <rect x="7" y="1" width="1" height="2"/>
      <rect x="10" y="1" width="1" height="2"/>
      <rect x="6" y="3" width="2" height="1"/>
      <rect x="10" y="3" width="2" height="1"/>
      <rect x="4" y="4" width="10" height="1"/>
      <rect x="3" y="5" width="1" height="3"/>
      <rect x="14" y="5" width="1" height="3"/>
      <rect x="7" y="6" width="2" height="1"/>
      <rect x="4" y="8" width="2" height="1"/>
      <rect x="12" y="8" width="2" height="1"/>
      <rect x="6" y="9" width="6" height="1"/>
      <rect x="6" y="10" width="1" height="1"/>
      <rect x="11" y="10" width="1" height="1"/>
      <rect x="5" y="11" width="2" height="1"/>
      <rect x="11" y="11" width="2" height="1"/>
      <rect x="5" y="12" width="1" height="2"/>
      <rect x="12" y="12" width="1" height="2"/>
      <rect x="4" y="14" width="3" height="1"/>
      <rect x="9" y="14" width="4" height="1"/>
      <rect x="3" y="15" width="1" height="1"/>
      <rect x="6" y="15" width="1" height="1"/>
      <rect x="9" y="15" width="1" height="1"/>
      <rect x="12" y="15" width="1" height="1"/>
    </g>
    <g fill="#C4BBFF">
      <rect x="8" y="1" width="2" height="2"/>
      <rect x="8" y="3" width="2" height="1"/>
      <rect x="4" y="5" width="10" height="1"/>
      <rect x="4" y="6" width="3" height="1"/>
      <rect x="9" y="6" width="5" height="1"/>
      <rect x="4" y="7" width="10" height="1"/>
      <rect x="6" y="8" width="6" height="1"/>
      <rect x="7" y="10" width="4" height="1"/>
      <rect x="7" y="11" width="4" height="1"/>
      <rect x="6" y="12" width="6" height="1"/>
      <rect x="6" y="13" width="1" height="1"/>
      <rect x="9" y="13" width="3" height="1"/>
    </g>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Navigation                                                         */
/* ------------------------------------------------------------------ */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'About', href: '/' },
    { label: 'Stack', href: '/stack' },
    { label: 'Get Started', href: '/get-started' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background/80 backdrop-blur-xl border-b border-border/50'
          : 'bg-transparent'
      }`}
    >
      <div className="container grid grid-cols-[1fr_1fr] lg:grid-cols-[1fr_auto_1fr] items-center h-16">
        <div className="flex items-center">
          <a href="/" className="flex items-center gap-2.5 group shrink-0">
            <NoriLogo className="w-7 h-7 transition-transform group-hover:scale-110 shrink-0" />
            <span className="font-semibold text-lg tracking-tight whitespace-nowrap">
              nostr-station
            </span>
          </a>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center justify-end gap-3">
          <a
            href="https://github.com/jared-logan/nostr-station"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex"
          >
            <button className="inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors">
              <GithubIcon className="w-5 h-5" />
            </button>
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden border-t border-border/50">
          <nav className="container py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-accent/50"
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://github.com/jared-logan/nostr-station"
              target="_blank"
              rel="noopener noreferrer"
              className="sm:hidden text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-accent/50 flex items-center gap-2"
            >
              <GithubIcon className="w-4 h-4" />
              GitHub
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

/* ------------------------------------------------------------------ */
/*  Hero Section                                                       */
/* ------------------------------------------------------------------ */
const HeroSection = () => {
  return (
    <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-16">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,hsl(265_85%_60%/0.15),transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_600px_at_50%_50%,hsl(265_70%_55%/0.08),transparent)]" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(hsl(265 85% 60%) 1px, transparent 1px), linear-gradient(90deg, hsl(265 85% 60%) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="container relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            Git over Nostr
          </span>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
            Version control, <span className="text-gradient">redecentralized</span>
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            ngit brings git workflows to Nostr. Repositories, patches, issues, and code review — all signed by your cryptographic identity and stored on relays you control.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Features Section                                                   */
/* ------------------------------------------------------------------ */
const features = [
  {
    icon: GitBranch,
    title: 'Native git over Nostr',
    description: 'Clone, push, pull, and merge using familiar git commands. Under the hood, commits and refs are published as signed Nostr events rather than stored on a centralized forge.',
    color: 'hsl(265 85% 60%)',
  },
  {
    icon: Key,
    title: 'Amber-signed commits',
    description: 'Every commit is signed by your nsec via Amber on your phone. Your private key never touches the station. Verifiable authorship without trusting the host.',
    color: 'hsl(280 85% 60%)',
  },
  {
    icon: MessageSquare,
    title: 'Issues & proposals on relay',
    description: 'Open issues, submit patches, and review code — all as Nostr events. Discussion lives alongside the code on the same relays, not in a separate database.',
    color: 'hsl(295 85% 60%)',
  },
  {
    icon: Lock,
    title: 'No forge lock-in',
    description: 'Your repo lives on any Nostr relay that accepts it. Migrate between relays without losing history. No GitHub, GitLab, or Gitea account required.',
    color: 'hsl(250 85% 60%)',
  },
  {
    icon: Zap,
    title: 'Fast local relay',
    description: 'When paired with nostr-station\'s built-in relay, pushes are instant. No network round-trip to a distant server. Iterate at the speed of thought.',
    color: 'hsl(290 85% 60%)',
  },
  {
    icon: Shield,
    title: 'Censorship-resistant',
    description: 'Relay operators can\'t delete your commits without your key. Publish to multiple relays for redundancy. Your project history is as durable as the Nostr network.',
    color: 'hsl(260 85% 60%)',
  },
];

const FeaturesSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            Features
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Git, but <span className="text-gradient">sovereign</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className={`group relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                style={{ backgroundColor: `${feature.color}15` }}
              >
                <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
              </div>
              <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  How It Works Section                                               */
/* ------------------------------------------------------------------ */
const HowItWorksSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      icon: GitBranch,
      title: 'Initialize repo',
      description: 'Run ngit init in any git repository. This creates a Nostr-compatible remote and generates the repository announcement event (kind 30617).',
    },
    {
      icon: Code2,
      title: 'Push commits',
      description: 'Use normal git push. ngit intercepts the push, signs each commit with Amber, and publishes refs as Nostr events to your configured relays.',
    },
    {
      icon: MessageSquare,
      title: 'Collaborate',
      description: 'Others clone with ngit clone <npub>. They push proposals (kind 1617), open issues (kind 1621), and review with status updates (kinds 1630–1633).',
    },
  ];

  return (
    <section id="how-it-works" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(265_85%_60%/0.06),transparent)]" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            How it works
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Three steps to <span className="text-gradient">decentralized git</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {steps.map((step, index) => (
            <div
              key={step.title}
              className={`group relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                <step.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  NIP-34 Section                                                     */
/* ------------------------------------------------------------------ */
const Nip34Section = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const kinds = [
    { kind: '30617', name: 'Repository announcement', description: 'Publishes repo metadata, default branch, and relay hints.' },
    { kind: '1617', name: 'Patch / proposal', description: 'A signed git patch ready for review and merge.' },
    { kind: '1621', name: 'Issue', description: 'Bug reports and feature requests tied to a repository.' },
    { kind: '1630', name: 'Status: open', description: 'Marks a proposal or issue as open for discussion.' },
    { kind: '1631', name: 'Status: accepted', description: 'Maintainer accepts the proposal; ready to merge.' },
    { kind: '1632', name: 'Status: rejected', description: 'Maintainer declines the proposal with rationale.' },
    { kind: '1633', name: 'Status: closed', description: 'Issue resolved or proposal merged and closed.' },
  ];

  return (
    <section id="nip34" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            NIP-34
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Event <span className="text-gradient">kinds</span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            ngit uses NIP-34 to map git concepts onto Nostr events. Every action is a signed, verifiable event.
          </p>
        </div>

        <div className={`max-w-3xl mx-auto transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs">
                      Kind
                    </th>
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs">
                      Name
                    </th>
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs">
                      Purpose
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {kinds.map((k, index) => (
                    <tr
                      key={k.kind}
                      className={`border-b border-border/30 hover:bg-primary/5 transition-colors ${index === kinds.length - 1 ? 'border-b-0' : ''}`}
                    >
                      <td className="px-6 py-4 font-mono text-foreground whitespace-nowrap">
                        {k.kind}
                      </td>
                      <td className="px-6 py-4 text-foreground font-medium whitespace-nowrap">
                        {k.name}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {k.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  CTA Section                                                        */
/* ------------------------------------------------------------------ */
const CtaSection = () => {
  return (
    <section className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(265_85%_60%/0.08),transparent)]" />

      <div className="container relative text-center">
        <Sparkles className="w-12 h-12 text-primary mx-auto mb-6" />
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">
          Install nostr-station to try <span className="text-gradient">ngit</span>
        </h2>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
          One command gets you a complete local Nostr stack with git-over-Nostr ready to go.
        </p>
        <a href="/get-started">
          <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12 text-base">
            Get Started
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </a>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */
const Ngit = () => {
  useSeoMeta({
    title: 'ngit — Git over Nostr — nostr-station',
    description: 'Native git workflows over Nostr. Repositories, patches, issues, and code review — all signed by your cryptographic identity and stored on relays.',
    ogTitle: 'ngit — Git over Nostr — nostr-station',
    ogDescription: 'Native git workflows over Nostr. Repositories, patches, issues, and code review — all signed by your cryptographic identity and stored on relays.',
    twitterCard: 'summary_large_image',
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      <HeroSection />
      <FeaturesSection />
      <HowItWorksSection />
      <Nip34Section />
      <CtaSection />
      <SiteFooter />
    </div>
  );
};

export default Ngit;
